/**
 * Access — who can use the app, and who has.
 *
 * Owners of the two access groups only. Every change is made with the signed-in owner's
 * own Microsoft token, so the page can do nothing its viewer couldn't do in Entra
 * themselves. See docs/ACCESS.md.
 *
 * Copy is for the person managing access, not for us: no error codes, no ids.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAccess, type AccessBackend, type Authorize } from '@/access/AccessContext'
import {
  GROUPS,
  GraphError,
  departmentScope,
  describeGrant,
  formatEt,
  summarizeSignIns,
  type DepartmentGrant,
  type GroupKey,
  type Person,
  type SignInSummary,
} from '@/access/graph'

const EFFECT_NOTE = 'It takes effect the next time they sign in.'

type Load<T> = { state: 'loading' } | { state: 'ok'; data: T } | { state: 'error'; message: string }

function Notice({ what, why }: { what: string; why?: ReactNode }) {
  return (
    <div className="notice" style={{ marginTop: 18 }}>
      <p className="notice-what">{what}</p>
      {why && <p className="notice-why">{why}</p>}
    </div>
  )
}

export function Access() {
  const { owner, backend } = useAccess()

  if (owner === 'checking' || (owner === 'owner' && !backend)) {
    return <span className="eyebrow">Checking your access…</span>
  }
  if (owner === 'not-owner' || !backend) {
    return (
      <>
        <h1 className="page-title">Access</h1>
        <Notice
          what="You don’t manage access to this app."
          why="Access is managed by the owners of the app’s two access groups. Ask one of them if someone needs to be added."
        />
      </>
    )
  }
  return <Manage backend={backend} />
}

function Manage({ backend }: { backend: AccessBackend }) {
  const [auth, setAuth] = useState<Authorize | 'pending' | 'error'>('pending')

  const authorize = useCallback(
    (interactive: boolean) => {
      setAuth('pending')
      backend
        .authorize(interactive)
        .then(setAuth)
        .catch(() => setAuth('error'))
    },
    [backend],
  )
  useEffect(() => authorize(false), [authorize])

  return (
    <>
      <h1 className="page-title">Access</h1>
      <p className="body-note" style={{ marginTop: 6 }}>
        Who can open this app. Full access sees every employee; Users see only the
        departments granted to them.
      </p>

      {auth === 'pending' && <p className="eyebrow" style={{ marginTop: 18 }}>Loading…</p>}
      {auth === 'consent' && (
        <Notice
          what="Waiting for IT approval"
          why="This page needs permission from IT to read and change the access groups. Nothing is wrong with your account. It will work here as soon as IT approves."
        />
      )}
      {auth === 'interaction' && (
        <div className="notice" style={{ marginTop: 18 }}>
          <p className="notice-what">Microsoft needs you to confirm it’s you.</p>
          <div>
            <button className="btn" onClick={() => authorize(true)}>
              Continue
            </button>
          </div>
        </div>
      )}
      {auth === 'error' && (
        <div className="notice" style={{ marginTop: 18 }}>
          <p className="notice-what">Couldn’t reach Microsoft.</p>
          <div>
            <button className="btn" onClick={() => authorize(false)}>
              Try again
            </button>
          </div>
        </div>
      )}
      {auth === 'ok' && <Loaded backend={backend} />}
    </>
  )
}

const errorText = (e: unknown) =>
  e instanceof GraphError && e.status === 403
    ? 'Microsoft refused this for your account.'
    : 'Couldn’t load this. Refresh to try again.'

function Loaded({ backend }: { backend: AccessBackend }) {
  const { graph } = backend
  const [full, setFull] = useState<Load<Person[]>>({ state: 'loading' })
  const [users, setUsers] = useState<Load<Person[]>>({ state: 'loading' })
  const [signIns, setSignIns] = useState<Load<SignInSummary[]> | { state: 'norole' }>({
    state: 'loading',
  })
  const [flash, setFlash] = useState<string | null>(null)

  const loadMembers = useCallback(
    (key: GroupKey) => {
      const set = key === 'full' ? setFull : setUsers
      graph
        .members(key)
        .then((data) => set({ state: 'ok', data }))
        .catch((e) => set({ state: 'error', message: errorText(e) }))
    },
    [graph],
  )

  useEffect(() => {
    loadMembers('full')
    loadMembers('users')
    graph
      .signIns(new Date(Date.now() - 30 * 86400_000))
      .then((rows) => setSignIns({ state: 'ok', data: summarizeSignIns(rows) }))
      .catch((e) =>
        setSignIns(
          e instanceof GraphError && e.status === 403
            ? { state: 'norole' }
            : { state: 'error', message: errorText(e) },
        ),
      )
  }, [graph, loadMembers])

  const lastSeen = useMemo(() => {
    const m = new Map<string, string | null>()
    if (signIns.state === 'ok') for (const s of signIns.data) m.set(s.userId, s.lastSuccess)
    return m
  }, [signIns])

  const inGroup = useMemo(() => {
    const m = new Map<string, GroupKey>()
    if (users.state === 'ok') for (const p of users.data) m.set(p.id, 'users')
    if (full.state === 'ok') for (const p of full.data) m.set(p.id, 'full')
    return m
  }, [full, users])

  const add = async (p: { id: string; name: string }) => {
    await graph.addMember(GROUPS.users.id, p.id)
    setFlash(`${p.name} added to Users. ${EFFECT_NOTE}`)
    loadMembers('users')
  }
  const remove = async (p: Person) => {
    await graph.removeMember(GROUPS.users.id, p.id)
    setFlash(`${p.name} removed from Users. ${EFFECT_NOTE}`)
    loadMembers('users')
  }

  return (
    <>
      {flash && (
        <div
          role="status"
          className="card"
          style={{
            marginTop: 18,
            borderColor: 'var(--good)',
            background: 'var(--good-bg)',
            color: 'var(--good-ink)',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <span style={{ flex: 1 }}>{flash}</span>
          <button className="btn" onClick={() => setFlash(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
          gap: 16,
          marginTop: 20,
        }}
      >
        <MemberCard
          group="full"
          load={full}
          lastSeen={lastSeen}
          signInsKnown={signIns.state === 'ok'}
        />
        <MemberCard
          group="users"
          load={users}
          lastSeen={lastSeen}
          signInsKnown={signIns.state === 'ok'}
          onRemove={remove}
          search={<AddPerson backend={backend} inGroup={inGroup} onAdd={add} />}
        />
      </div>

      <section className="section">
        <h2 className="section-heading">Department scope</h2>
        <p className="body-note" style={{ marginTop: 4 }}>
          What each person in Users can see. Grants are managed in RefUserSecurity, not here.
        </p>
        <DepartmentScope backend={backend} users={users} />
      </section>

      <section className="section">
        <h2 className="section-heading">Recent sign-ins</h2>
        <p className="body-note" style={{ marginTop: 4 }}>
          The last 30 days. Times are Eastern.
        </p>
        <SignIns load={signIns} inGroup={inGroup} onAdd={add} />
      </section>
    </>
  )
}

// ------------------------------------------------------------------ members

function MemberCard({
  group,
  load,
  lastSeen,
  signInsKnown,
  onRemove,
  search,
}: {
  group: GroupKey
  load: Load<Person[]>
  lastSeen: Map<string, string | null>
  signInsKnown: boolean
  onRemove?: (p: Person) => Promise<void>
  search?: ReactNode
}) {
  const g = GROUPS[group]
  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h3 className="card-heading">{g.label}</h3>
        {load.state === 'ok' && <span className="pill">{load.data.length}</span>}
      </div>
      <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-2)' }}>
        {g.gives}
        {!g.editable && ' Changed by IT; it can’t be edited here.'}
      </p>
      {search}
      <div style={{ marginTop: 12 }}>
        {load.state === 'loading' && <span className="eyebrow">Loading…</span>}
        {load.state === 'error' && <p className="body-note">{load.message}</p>}
        {load.state === 'ok' && load.data.length === 0 && (
          <p className="empty-note" style={{ padding: 14 }}>No one yet.</p>
        )}
        {load.state === 'ok' && load.data.length > 0 && (
          <div className="dt-wrap">
            <table className="dt">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Last sign-in</th>
                  {onRemove && <th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {load.data.map((p) => (
                  <MemberRow
                    key={p.id}
                    person={p}
                    last={signInsKnown ? (lastSeen.get(p.id) ?? null) : undefined}
                    onRemove={onRemove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function PersonCell({ name, email }: { name: string; email: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div>{name || email}</div>
      <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
        {email}
      </div>
    </div>
  )
}

function MemberRow({
  person,
  last,
  onRemove,
}: {
  person: Person
  /** undefined: sign-in history unavailable. null: none in 30 days. */
  last: string | null | undefined
  onRemove?: (p: Person) => Promise<void>
}) {
  return (
    <tr>
      <td>
        <PersonCell name={person.name} email={person.email} />
      </td>
      <td className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--ink-2)' }}>
        {last === undefined ? '—' : last === null ? 'Not in 30 days' : formatEt(last)}
      </td>
      {onRemove && (
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <Confirm
            label="Remove"
            question={`Remove ${person.name || person.email} from Users?`}
            confirm="Remove"
            danger
            onConfirm={() => onRemove(person)}
          />
        </td>
      )}
    </tr>
  )
}

/**
 * A button that asks before it acts. The question replaces the button in place, so the
 * confirm sits exactly where the person was already looking.
 */
function Confirm({
  label,
  question,
  confirm,
  danger = false,
  onConfirm,
}: {
  label: string
  question: string
  confirm: string
  danger?: boolean
  onConfirm: () => Promise<void>
}) {
  const [stage, setStage] = useState<'idle' | 'asking' | 'busy' | 'failed'>('idle')
  const mounted = useRef(true)
  useEffect(() => () => void (mounted.current = false), [])

  if (stage === 'idle' || stage === 'failed') {
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        {stage === 'failed' && (
          <span style={{ fontSize: 'var(--t-micro)', color: 'var(--crit-ink)' }}>
            Didn’t work. Try again?
          </span>
        )}
        <button className="btn" onClick={() => setStage('asking')}>
          {label}
        </button>
      </span>
    )
  }
  return (
    <span
      role="group"
      aria-label={question}
      style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}
    >
      <span style={{ fontSize: 13 }}>{question}</span>
      <button
        className="btn"
        disabled={stage === 'busy'}
        style={
          danger
            ? { background: 'var(--crit-bg)', borderColor: 'var(--crit)', color: 'var(--crit-ink)' }
            : { background: 'var(--accent-soft)', borderColor: 'var(--accent)', color: 'var(--accent-ink)' }
        }
        onClick={() => {
          setStage('busy')
          onConfirm().then(
            () => mounted.current && setStage('idle'),
            () => mounted.current && setStage('failed'),
          )
        }}
      >
        {stage === 'busy' ? 'Working…' : confirm}
      </button>
      <button className="btn" disabled={stage === 'busy'} onClick={() => setStage('idle')}>
        Cancel
      </button>
    </span>
  )
}

function AddPerson({
  backend,
  inGroup,
  onAdd,
}: {
  backend: AccessBackend
  inGroup: Map<string, GroupKey>
  onAdd: (p: Person) => Promise<void>
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Load<Person[]> | null>(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setResults(null)
      return
    }
    let cancelled = false
    const t = setTimeout(() => {
      setResults({ state: 'loading' })
      backend.graph
        .search(term)
        .then((data) => !cancelled && setResults({ state: 'ok', data }))
        .catch((e) => !cancelled && setResults({ state: 'error', message: errorText(e) }))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q, backend])

  return (
    <div style={{ marginTop: 12 }}>
      <label className="eyebrow" htmlFor="access-search" style={{ display: 'block', marginBottom: 5 }}>
        Add someone
      </label>
      <input
        id="access-search"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name or email"
        autoComplete="off"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '7px 10px',
          fontSize: 'var(--t-table)',
          border: '1px solid var(--rule-2)',
          borderRadius: 6,
          background: 'var(--surface)',
          color: 'var(--ink)',
        }}
      />
      {results && (
        <div
          style={{
            marginTop: 6,
            border: '1px solid var(--rule)',
            borderRadius: 8,
            background: 'var(--surface-2)',
          }}
        >
          {results.state === 'loading' && (
            <p className="eyebrow" style={{ margin: 0, padding: 10 }}>Searching…</p>
          )}
          {results.state === 'error' && <p style={{ margin: 0, padding: 10 }}>{results.message}</p>}
          {results.state === 'ok' && results.data.length === 0 && (
            <p style={{ margin: 0, padding: 10, color: 'var(--ink-3)', fontSize: 13 }}>No one found.</p>
          )}
          {results.state === 'ok' &&
            results.data.map((p) => {
              const has = inGroup.get(p.id)
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '7px 10px',
                    borderTop: '1px solid var(--rule)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PersonCell name={p.name} email={p.email} />
                  </div>
                  {has ? (
                    <span className="pill">{has === 'users' ? 'In Users' : 'Has full access'}</span>
                  ) : (
                    <Confirm
                      label="Add"
                      question={`Add ${p.name || p.email} to Users?`}
                      confirm="Add"
                      onConfirm={async () => {
                        await onAdd(p)
                        setQ('')
                      }}
                    />
                  )}
                </div>
              )
            })}
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ department scope

function DepartmentScope({ backend, users }: { backend: AccessBackend; users: Load<Person[]> }) {
  const [grants, setGrants] = useState<Load<DepartmentGrant[]> | { state: 'forbidden' }>({
    state: 'loading',
  })
  const emails = users.state === 'ok' ? users.data.map((p) => p.email).join('\n') : null

  useEffect(() => {
    if (emails === null) return
    if (emails === '') {
      setGrants({ state: 'ok', data: [] })
      return
    }
    let cancelled = false
    setGrants({ state: 'loading' })
    backend
      .grants(emails.split('\n'))
      .then((r) => {
        if (cancelled) return
        if (r.ok) setGrants({ state: 'ok', data: r.grants })
        else if (r.status === 403) setGrants({ state: 'forbidden' })
        else setGrants({ state: 'error', message: 'Couldn’t load department grants.' })
      })
      .catch(() => !cancelled && setGrants({ state: 'error', message: 'Couldn’t load department grants.' }))
    return () => {
      cancelled = true
    }
  }, [emails, backend])

  if (users.state !== 'ok') return null
  if (users.data.length === 0) {
    return <p className="empty-note">No one is in Users yet.</p>
  }
  if (grants.state === 'loading') return <p className="eyebrow" style={{ marginTop: 14 }}>Loading…</p>
  if (grants.state === 'forbidden') {
    return <Notice what="You need full access to see department grants." />
  }
  if (grants.state === 'error') return <p className="body-note">{grants.message}</p>

  const rows = departmentScope(users.data, grants.data)
  return (
    <div className="dt-wrap card" style={{ marginTop: 14, padding: 0 }}>
      <table className="dt">
        <thead>
          <tr>
            <th>Person</th>
            <th>Can see</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.person.id}>
              <td style={{ width: '40%' }}>
                <PersonCell name={r.person.name} email={r.person.email} />
              </td>
              <td>
                {r.noGrant ? (
                  <span
                    className="badge badge--crit"
                    style={{ fontFamily: 'var(--sans)', fontWeight: 500, fontSize: 13, justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    No department granted. They will see no data until a department is
                    granted in RefUserSecurity.
                  </span>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {r.grants.map((g, i) => (
                      <li key={i}>{describeGrant(g)}</li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ------------------------------------------------------------------ sign-ins

function SignIns({
  load,
  inGroup,
  onAdd,
}: {
  load: Load<SignInSummary[]> | { state: 'norole' }
  inGroup: Map<string, GroupKey>
  onAdd: (p: Person) => Promise<void>
}) {
  if (load.state === 'loading') return <p className="eyebrow" style={{ marginTop: 14 }}>Loading…</p>
  if (load.state === 'norole') {
    return (
      <Notice
        what="Sign-in history needs the Reports Reader role."
        why="Ask IT to give you the Reports Reader role in Entra. Everything else on this page works without it."
      />
    )
  }
  if (load.state === 'error') return <p className="body-note">{load.message}</p>
  if (load.data.length === 0) return <p className="empty-note">No sign-ins in the last 30 days.</p>

  return (
    <div className="dt-wrap card" style={{ marginTop: 14, padding: 0 }}>
      <table className="dt">
        <thead>
          <tr>
            <th>Person</th>
            <th className="num">Sign-ins</th>
            <th className="num">Days</th>
            <th>First</th>
            <th>Last</th>
            <th className="num">Failed</th>
            <th aria-label="Status" />
          </tr>
        </thead>
        <tbody>
          {load.data.map((s) => {
            const refused = s.notAssigned && !inGroup.has(s.userId)
            return (
              <tr key={s.userId || s.email}>
                <td>
                  <PersonCell name={s.name} email={s.email} />
                </td>
                <td className="num">{s.signIns}</td>
                <td className="num">{s.days}</td>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>{formatEt(s.first)}</td>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>{formatEt(s.last)}</td>
                <td className="num" style={{ color: s.failures ? 'var(--crit-ink)' : undefined }}>
                  {s.failures}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {refused && (
                    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <span className="badge badge--crit" style={{ fontFamily: 'var(--sans)', fontWeight: 500, fontSize: 12 }}>
                        Turned away: no access
                      </span>
                      <Confirm
                        label="Add to Users"
                        question={`Add ${s.name || s.email} to Users?`}
                        confirm="Add"
                        onConfirm={() => onAdd({ id: s.userId, name: s.name, email: s.email })}
                      />
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
